import "aos/dist/aos.css";
import { useEffect, useRef, useState } from "react";
import SwiperCore, { Navigation, Pagination } from "swiper";
import "swiper/css/navigation";
import "swiper/swiper.min.css";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { differentKeywordArray } from "../../array/genwaveArray";
import BottomBanner from "../../components/common/BottomBanner";
import HomeButton from "../../components/common/HomeButtons";
import ShortcutBanner from "../../components/common/ShortcutBanner";
import SubPageDot from "../../components/common/SubpageDot";
import SubpageTitle from "../../components/common/SubpageTitle";
import { userinfoAtom } from "../../store";

SwiperCore.use([Navigation, Pagination]);

export default function Genwave() {
  const componentRef = useRef(null);
  const { t } = useTranslation();
  const { language } = useRecoilValue(userinfoAtom);
  useEffect(() => {
    var AOS = require("aos");
    AOS.init({
      duration: 1000,
    });
  }, []);

  return (
    <main className="bg-black pt-[200px] max-md:pt-[98px] pb-10 px-0 max-md:overflow-x-hidden overflow-hidden">
      <ShortcutBanner
        firstButton={t("GenWave 바로가기")}
        secondButton={t("서비스 이용안내")}
        firstLink="https://genwave.ploonet.com"
        secondLink="https://genwave.ploonet.com"
        componentRef={componentRef}
        title={t("GenWave")}
      />
      <div className="max-w-[1600px] max-2xl:max-w-[1200px] mx-auto space-y-48 max-md:space-y-20">
        <MainSection language={language} componentRef={componentRef} />
        <div data-aos="fade-up">
          <UsesSection />
        </div>
        <div className="max-md:px-5" data-aos="fade-up">
          <DifferenceSection />
        </div>
        <div className="max-md:px-5" data-aos="fade-up">
          <PreviewSection />
        </div>
        <div className="max-md:px-5">
          <BottomBanner
            topText={t("GenWave와 함께")}
            bottomText={t(
              "당신의 아이디어 하나로 콘텐츠 크리에이터가 되어 보세요."
            )}
            serviceName="GenWave"
            url="https://genwave.ploonet.com"
          />
        </div>
      </div>
    </main>
  );
}

function MainSection({
  componentRef,
  language,
}: {
  componentRef: React.MutableRefObject<null>;
  language: "kr" | "en";
}) {
  return (
    <section className="max-md:px-5">
      <div className="max-w-[1600px] mx-auto z-10 flex max-md:flex-col-reverse items-center justify-between max-md:items-end">
        <div
          data-aos="fade-right"
          className="w-full min-h-fit flex flex-col items-start"
        >
          <div className="space-y-10 max-md:space-y-0 max-md:mt-12">
            <SubpageTitle>GenWave</SubpageTitle>
            <div className="text-white text-5xl max-2xl:text-4xl leading-tight max-md:text-2xl max-md:!leading-9 max-md:space-y-0 max-md:!mt-2 whitespace-pre-wrap">
              <h1 className="font-extralight">
                {t("키워드 하나만 입력해도 완성형 프롬프트 자동 생성")}
              </h1>
              <h1 className="font-bold">
                {t("AI가 척척 알아서 생성해주는 멀티모달 AI 콘텐츠 플랫폼")}
              </h1>
            </div>
            <p className="text-white/70 font-extralight text-xl max-2xl:text-lg max-md:text-base mt-8 max-w-[630px] max-2xl:max-w-lg max-md:!mt-4">
              {t(
                "처음 콘텐츠를 만들고 싶은데, 어떤 툴을 써야 할지, 어떤 프롬프트를 써야 할지 막막하셨나요?"
              )}
              <br />
              {t(
                "GenWave는 단 하나의 키워드만 입력하면 AI가 이미지부터 영상, 음악, 캐릭터, 숏폼까지"
              )}
              <br />
              {t(
                "모두 자동으로 만들어주는 세상에서 가장 쉬운 생성형 콘텐츠 플랫폼입니다."
              )}
            </p>
          </div>
          <div
            ref={componentRef}
            className="space-x-5 max-md:space-x-3 flex mt-[60px] max-md:mt-10"
          >
            <HomeButton
              onClick={() => window.open("https://genwave.ploonet.com")}
              icon={true}
            >
              {t("GenWave 바로가기")}
            </HomeButton>
            <div className="p-[1px] bg-gradient-to-br from-white to-white rounded-lg hover:from-primary hover:to-primaryTo">
              <HomeButton
                onClick={() => window.open("https://coffee-pressure-f38.notion.site/genwave-guide")}
                icon={true}
                color="main white"
              >
                {t("서비스 이용안내")}
              </HomeButton>
            </div>
          </div>
        </div>
        <div
          data-aos-duration="2000"
          data-aos="fade-left"
          className="relative w-full"
        >
          <div className="flex relative w-full">
            <div className="swiper-animation-studio fade-in-left w-full">
              <iframe
                className="w-full aspect-video md:-mt-16"
                src="https://www.youtube.com/embed/jE-x_XYlZos?si=IKlJReL7MKnOtrxa&amp;controls=0&amp;autoplay=1&amp;loop=1&amp;mute=1&amp;playlist=jE-x_XYlZos"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function UsesSection() {
  const [activeTab, setActiveTab] = useState(0);

  const useCases = [
    {
      title: "프롬프트 자동 생성",
      subtitle: "키워드만 입력하면, AI가 알아서 프롬프트를 만들어줍니다",
      description: "복잡한 입력 없이도 콘텐츠 시작할 수 있어요",
      icon: "/genwave/subpage/check.svg",
      leftContent: {
        title: "프롬프트 자동 생성 기술",
        subtitle:
          "처음 AI를 써보는 사람도 쉽게 시작할 수 있어요.키워드 하나만 입력하면, 주제에 맞는 고품질 프롬프트를 대신 작성해줍니다.어떻게 써야 할지 몰라서 멈췄던 순간, 이제는 사라집니다.",
        description: "복잡한 입력 없이도 콘텐츠 제작이 가능합니다",
        icon: "/genwave/subpage/magic.svg",
      },
    },
    {
      title: "기술 통합형 플랫폼",
      subtitle: "툴마다 따로 쓰지 않아도, 다양한 생성 기술을 하나로 연결합니다",
      description: "다양한 생성형 AI 기술을 GenWave 하나에 모두 연결했어요",
      icon: "/genwave/subpage/generate.svg",
      leftContent: {
        title: "생성형 AI 통합 기술 플랫폼",
        subtitle:
          "GenWave는 다양한 생성형 AI 기술(API 기반)을 하나의 플랫폼에 통합해 제공합니다.복잡한 설정이나 툴 전환 없이, 한 번의 입력으로 여러 작업을 자동화된 흐름으로 처리할 수 있어 효율적인 콘텐츠 제작이 가능합니다.",
        description: "툴마다 따로 쓸 필요 없이, GenWave가 모두 통합해드립니다",
        icon: "/genwave/subpage/magic.svg",
      },
    },
    {
      title: "멀티모달 콘텐츠 생성",
      subtitle: "이미지, 영상, 음악, 캐릭터, 숏폼까지 한 번에 생성합니다",
      description: "키워드 하나만 모든 형식의 결과물을 완성할 수 있어요",
      icon: "/genwave/subpage/magic.svg",
      leftContent: {
        title: "멀티모달 기반 콘텐츠 생성 기술",
        subtitle:
          "이미지, 영상, BGM, 음악, 캐릭터, 숏폼까지다양한 콘텐츠 포맷을 키워드 한 번으로 자동 생성합니다.여러 툴 없이, GenWave 하나로 멀티모달 콘텐츠를 완성하세요.",
        description: "키워드 하나로, 전 포맷의 콘텐츠를 동시에 생성합니다.",
        icon: "/genwave/subpage/magic.svg",
      },
    },
  ];

  return (
    <>
      <section className="space-y-14 max-md:space-y-5 max-md:pl-5">
        <div className="flex justify-between">
          <h1 className="text-5xl text-white max-2xl:text-4xl max-md:text-2xl flex items-start max-md:items-baseline max-md:flex-col max-md:space-y-1 font-extralight">
            <SubPageDot />
            <span className="font-extralight whitespace-pre-wrap leading-tight">
              <strong className="font-bold">{t("GenWave")}</strong>
              {t("는 이렇게 다릅니다")}
            </span>
          </h1>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-2 max-md:grid-cols-1 gap-5 max-md:gap-8 max-md:mr-4 mt-20 max-md:mt-10 items-start">
          {/* Left Content Area */}
          <div className="h-full max-md:hidden">
            <div className="bg-[#111] rounded-[20px] border border-black2 p-8 max-md:p-6 h-full">
              <div className="w-full h-auto rounded-lg mb-8 overflow-hidden">
                {activeTab === 0 && (
                  <video
                    className="w-full h-full object-cover rounded-lg"
                    src="/genwave/subpage/1.mp4"
                    autoPlay
                    loop
                    muted
                    controls={false}
                  />
                )}
                {activeTab === 1 && (
                  <video
                    className="w-full h-full object-cover rounded-lg"
                    src="/genwave/subpage/2.mp4"
                    autoPlay
                    loop
                    muted
                    controls={false}
                  />
                )}
                {activeTab === 2 && (
                  <iframe
                    className="w-full h-[300px] rounded-lg mt-10"
                    src="https://www.youtube.com/embed/NVSS8FAYs6c?si=nS3lit4Hj_OJWBhY&autoplay=1&loop=1&mute=1&controls=0&playlist=NVSS8FAYs6c"
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  />
                )}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t(useCases[activeTab].leftContent.title)}
              </h3>
              <p className="text-white/80 text-md mb-4">
                {t(useCases[activeTab].leftContent.subtitle)}
              </p>

              <div className="flex flex-row gap-2 items-start justify-start">
                <img
                  src={useCases[activeTab].icon}
                  alt=""
                  className="w-6 h-6"
                />
                <p className="text-sm transition-colors mt-1 text-transparent bg-clip-text bg-gradient-to-r from-[#FF4E83] to-[#FFBB54]">
                  {t(useCases[activeTab].leftContent.description)}
                </p>
              </div>
            </div>
          </div>

          {/* Right Tab Area */}
          <div className="flex flex-col h-full">
            {useCases.map((useCase, idx) => (
              <div
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`
                  cursor-pointer py-12 px-8 rounded-[20px] border transition-all duration-300 flex-1 bg-[#111]
                  ${
                    activeTab === idx
                      ? "border-primary"
                      : "border-black2 hover:border-primary"
                  }
                  ${idx < useCases.length - 1 ? "mb-4" : ""}
                `}
              >
                <div className="flex items-start space-x-4 h-full">
                  <div className="flex-1">
                    {/* Desktop view - use original useCase data */}
                    <div className="md:block hidden">
                      <h4 className="text-xl font-bold mb-2 transition-colors text-white">
                        {t(useCase.title)}
                      </h4>
                      <p className="text-[#999] mb-5">{t(useCase.subtitle)}</p>
                      <div className="flex flex-row gap-2 items-start justify-start">
                        <img src={useCase.icon} alt="" className="w-6 h-6" />
                        <p className="text-sm transition-colors mt-1 text-transparent bg-clip-text bg-gradient-to-r from-[#FF4E83] to-[#FFBB54]">
                          {t(useCase.description)}
                        </p>
                      </div>
                    </div>

                    {/* Mobile view - use leftContent data */}
                    <div className="md:hidden block">
                      <h4 className="text-xl font-bold mb-2 transition-colors text-white">
                        {t(useCase.leftContent.title)}
                      </h4>
                      <p className="text-[#999] mb-5">
                        {t(useCase.leftContent.subtitle)}
                      </p>
                      <div className="flex flex-row gap-2 items-start justify-start">
                        <img src={useCase.icon} alt="" className="w-6 h-6" />
                        <p className="text-sm transition-colors mt-1 text-transparent bg-clip-text bg-gradient-to-r from-[#FF4E83] to-[#FFBB54]">
                          {t(useCase.leftContent.description)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function PreviewSection() {
  return (
    <section className="space-y-14 max-md:space-y-0 text-white">
      <div className="space-y-5">
        <h1 className="text-5xl text-white max-2xl:text-4xl max-md:text-2xl flex font-extralight max-md:flex-col max-md:space-y-1">
          <SubPageDot />
          <div className="max-md:flex-col max-md:flex">
            <span>{t("누구든, 어디서든, 무엇이든")} </span>
            <span className="font-bold">{t("경계 없이 확장됩니다")}</span>
          </div>
        </h1>
      </div>
      <div className="space-y-32 max-2xl:space-y-20 max-md:!mt-5">
        <section className="flex max-md:flex-col max-md:space-x-0 space-x-60 max-2xl:space-x-24">
          <div className="w-full h-auto">
            <img src="/genwave/subpage/phone.png" />
          </div>
          <div className="w-full h-auto">
            <div className="flex items-start space-x-4 h-full">
              <div className="flex-1">
                <h4 className="text-xl font-bold mb-5 transition-colors text-transparent bg-clip-text bg-gradient-to-br from-[#FF4E83] to-[#FFBB54]">
                  Contens AI
                </h4>
                <h4 className="text-2xl font-bold mb-2 transition-colors text-white">
                  {t("초보자도, 전문가도")}
                  <br />
                  {t("누구나 바로 시작할 수 있는 콘텐츠 생성 도구")}
                </h4>
                <p className="text-[#999] mb-5">
                  {t(
                    "영상 편집이 처음인 사람도, 콘셉트만 필요한 마케터도,팬들과 콘텐츠로 소통하고 싶은 인플루언서도GenWave는 전문 스킬과 상관없이 바로 사용 가능한 AI 플랫폼입니다.별도의 학습 없이도 키워드 하나로 콘텐츠가 시작됩니다."
                  )}
                </p>
                <div className="flex flex-col gap-2 items-start justify-start">
                  <p className="text-sm transition-colors mt-1 text-primary">
                    {t("활용 가능 대상")}
                  </p>
                  <p className="text-sm transition-colors mt-1 text-white">
                    {t("초보자 · 인플루언서 · 마케터 · 1인 콘텐츠 운영자")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="space-y-32 max-2xl:space-y-20 max-md:!mt-5 pt-20">
        <section className="flex max-md:flex-col-reverse max-md:space-x-0 space-x-60 max-2xl:space-x-24">
          <div className="w-full h-auto">
            <div className="flex items-start space-x-4 h-full">
              <div className="flex-1">
                <h4 className="text-xl font-bold mb-5 transition-colors text-transparent bg-clip-text bg-gradient-to-br from-[#FF4E83] to-[#FFBB54]">
                  Multi-language Ready
                </h4>
                <h4 className="text-2xl font-bold mb-2 transition-colors text-white">
                  {t("글로벌 크리에이터를 위한")}
                  <br />
                  {t("다국어 콘텐츠 제작 지원")}
                </h4>
                <p className="text-[#999] mb-5">
                  {t(
                    "국내뿐 아니라 전 세계 유저를 위한다국어 콘텐츠를 AI가 자동으로 생성하거나 번역해 줍니다.언어의 장벽 없이 누구나 글로벌 콘텐츠를 제작할 수 있습니다."
                  )}
                </p>
                <div className="flex flex-col gap-2 items-start justify-start">
                  <p className="text-sm transition-colors mt-1 text-primary">
                    {t("활용 가능 대상")}
                  </p>
                  <p className="text-sm transition-colors mt-1 text-white">
                    {t("해외 대상 마케팅 · 글로벌 유튜버 · 브랜드 운영자")}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full h-auto rounded-[20px]">
            <iframe
              className="w-full h-[300px]"
              src="https://www.youtube.com/embed/NVSS8FAYs6c?si=nS3lit4Hj_OJWBhY&autoplay=1&loop=1&mute=1&controls=0&playlist=NVSS8FAYs6c"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            />
          </div>
        </section>
      </div>
    </section>
  );
}

function DifferenceSection() {
  return (
    <section className="text-white">
      <h1 className="text-5xl text-white max-2xl:text-4xl max-md:text-2xl flex font-extralight mb-[60px] max-md:mb-5 max-md:flex-col max-md:space-y-1">
        <SubPageDot />
        <div className="max-md:flex max-md:flex-col">
          <span>{t("당신의 키워드로 만들어지는")}</span>
          <span className="font-bold">{t("6가지 콘텐츠")}</span>
        </div>
      </h1>

      <section className="grid grid-cols-3 max-md:grid-cols-1 gap-10 max-2xl:gap-4">
        {differentKeywordArray.map(({ icon, keyPhrase, description }, idx) => (
          <div
            key={`difference-${idx}`}
            className="flex items-center bg-[#141414] rounded-[20px] border border-black2 w-full py-8 px-12 max-md:py-7 max-md:px-6 max-2xl:px-5 space-x-8 max-2xl:space-x-6"
          >
            <div className="w-20 h-20 rounded-full bg-[#222] flex items-center justify-center shrink-0">
              <img src={icon} className="w-10 h-10 object-contain" />
            </div>
            <div className="flex flex-col space-y-3 max-md:space-y-2">
              <h1 className="font-bold text-2xl max-2xl:text-xl max-md:text-lg text-white">
                {t(keyPhrase)}
              </h1>
              <span className="block text-white/60 max-2xl:text-sm font-extralight">
                {t(description)}
              </span>
            </div>
          </div>
        ))}
      </section>
    </section>
  );
}
